/**
 * Dashboard page component.
 *
 * Main dashboard page with foldable sidebar navigation for primary actions
 * and an insights column (performance trends + recent simulation history).
 *
 * Route: `/`
 * Requires authentication: Yes
 *
 * @module Dashboard
 */

import { useState, useEffect, useCallback } from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import {
  DashboardSidebar,
  DashboardNavMenuButton,
  DashboardNavList,
} from "@/components/dashboard/DashboardSidebar";
import { DashboardInsightsPanel } from "@/components/dashboard/DashboardInsightsPanel";
import { QuickStartExamCard } from "@/components/dashboard/QuickStartExamCard";
import { SimulationResumeCard } from "@/components/dashboard/SimulationResumeCard";
import { HumanReviewsWidget } from "@/components/dashboard/HumanReviewsWidget";
import { DiagnosticProgressWidget } from "@/components/dashboard/DiagnosticProgressWidget";
import { PlanBanner } from "@/components/dashboard/PlanBanner";
import { OnboardingPanel } from "@/components/onboarding/OnboardingPanel";
import { useDashboardModules } from "@/hooks/useDashboardModules";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

import { useTranslation } from "react-i18next";
import {
  readNavSidebarCollapsed,
  writeNavSidebarCollapsed,
} from "@/lib/sidebarNavStorage";
import { useQuickExamStartHref } from "@/hooks/useQuickExamStartHref";

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const quickExamStart = useQuickExamStartHref();
  const dashboardNavModules = useDashboardModules();

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (user?.id) {
      const onboardingKey = `deutschgenie_onboarding_completed_${user.id}`;
      const hasCompleted = localStorage.getItem(onboardingKey);
      if (!hasCompleted) {
        setShowOnboarding(true);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    setSidebarCollapsed(readNavSidebarCollapsed(user?.id));
  }, [user?.id]);

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      writeNavSidebarCollapsed(user?.id, next);
      return next;
    });
  }, [user?.id]);

  const handleOnboardingFinish = () => {
    if (user?.id) {
      const onboardingKey = `deutschgenie_onboarding_completed_${user.id}`;
      localStorage.setItem(onboardingKey, "true");
      setShowOnboarding(false);
    }
  };

  const username = user?.username || "User";

  const handleHelpClick = () => {
    setShowOnboarding(true);
  };

  return (
    <div className="min-h-[100dvh] bg-background">
      <OnboardingPanel
        open={showOnboarding}
        onOpenChange={setShowOnboarding}
        onFinish={handleOnboardingFinish}
      />

      <div className="flex min-h-[100dvh] flex-col lg:flex-row">
        <div className="hidden lg:sticky lg:top-0 lg:flex lg:h-[100dvh] lg:min-h-[100dvh] lg:shrink-0">
          <DashboardSidebar
            modules={dashboardNavModules}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={handleToggleSidebar}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="border-b border-border bg-background/95 px-3 py-3 sm:px-6 sm:py-4 lg:px-8">
            <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 sm:items-center">
                <DashboardNavMenuButton onOpen={() => setMobileNavOpen(true)} />
                <div>
                  <h1 className="font-ui text-xl font-bold text-foreground sm:text-2xl">
                    {t("nav.dashboard")}
                  </h1>
                  <p className="font-ui mt-0.5 text-xs text-muted-foreground sm:mt-1 sm:text-sm">
                    {t("dashboard.welcome", { name: username })}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-1 rounded-lg bg-muted/30 p-1 sm:justify-end sm:bg-transparent sm:p-0">
                <LanguageSwitcher />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleHelpClick}
                  aria-label="Help"
                >
                  <HelpCircle className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          <Dialog open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <DialogContent className="flex max-h-[85dvh] max-w-md flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
              <DialogHeader className="shrink-0 border-b border-border px-6 pb-4 pt-6">
                <DialogTitle className="font-ui">
                  {t("dashboard.quickAccess.title")}
                </DialogTitle>
              </DialogHeader>
              <DashboardNavList
                modules={dashboardNavModules}
                onNavigate={() => setMobileNavOpen(false)}
              />
            </DialogContent>
          </Dialog>

          <main className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
            <div className="flex min-w-0 flex-col gap-4">
              <PlanBanner />
              <SimulationResumeCard quickExamStart={quickExamStart} />
              <QuickStartExamCard quickExamStart={quickExamStart} />
              <HumanReviewsWidget />
              <DiagnosticProgressWidget />
              <DashboardInsightsPanel />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
